<template>
  <div class="page-shell evaluation-detail-page">
    <div class="topbar evaluation-detail-page__topbar">
      <div class="page-title evaluation-detail-page__title">
        <h1><span class="title-icon">📄</span>资产评估审核详情</h1>
        <p>{{ currentItem ? currentItem.assetName : '查看资产评估申请详情' }}</p>
      </div>
      <div class="evaluation-detail-page__topbar-actions">
        <button v-if="currentItem && currentItem.reportUrl" class="btn btn-outline" type="button" @click="openReport(currentItem.reportUrl)">下载报告</button>
        <button class="btn btn-outline" type="button" @click="backToList">返回列表</button>
      </div>
    </div>

    <div v-if="loading" class="card evaluation-state-card">
      加载中...
    </div>

    <template v-else-if="currentItem">
      <div class="page-hero evaluation-hero evaluation-hero--summary">
        <div class="evaluation-hero__main">
          <div class="evaluation-hero__badges">
            <span :class="statusClass(currentItem.status)">{{ currentItem.statusText }}</span>
            <span :class="['tag', currentItem.userType === 'mcn' ? 'tag-purple' : 'tag-blue']">{{ currentItem.userTypeText }}</span>
          </div>
          <h2 class="evaluation-hero__title">{{ currentItem.userType === 'mcn' ? '主播资产评估申请详情' : (currentItem.assetName || (currentItem.submitterName + '个人主播数字资产评估')) }}</h2>
        </div>
        <div :class="['summary-strip', { 'summary-strip--with-evidence': isApproved }]">
          <div v-for="field in summaryFields" :key="field.label" class="summary-strip__item">
            <span>{{ field.label }}</span>
            <strong>{{ field.value }}</strong>
          </div>
        </div>
      </div>

      <div v-if="!isMcn" class="card detail-section-card">
        <div class="section-head">
          <div class="section-head__index">1</div>
          <div class="section-head__content"><h3>资产基本信息</h3></div>
        </div>
        <div class="detail-panel">
          <div class="info-grid detail-panel__grid detail-panel__grid--personal">
            <div v-for="field in personalBasicFields" :key="field.label" class="info-item" :class="field.fullWidth ? 'full-width' : ''">
              <span class="ii-label">{{ field.label }}</span>
              <span class="ii-value">{{ field.value }}</span>
            </div>
          </div>
          <!-- <div class="tag-line" v-if="personalCoreVerticals.length"><span class="tag-line__label">核心垂类</span><div class="tag-line__content"><span v-for="item in personalCoreVerticals" :key="item" class="tag tag-orange">{{ item }}</span></div></div> -->
          <div class="tag-line"><span class="tag-line__label">主营内容方向</span><div class="tag-line__content"><span v-for="item in personalContentDirections" :key="item" class="tag tag-blue">{{ item }}</span></div></div>
          <!-- <div class="tag-line"><span class="tag-line__label">合作平台</span><div class="tag-line__content"><span v-for="item in personalHonorPlatforms" :key="item" class="tag tag-green">{{ item }}</span></div></div> -->
          <div class="tag-line" v-if="personalHonors.length"><span class="tag-line__label" style="width: 100px;">行业荣誉/认证</span><div class="tag-line__content"><span v-for="item in personalHonors" :key="item" class="tag tag-purple">{{ item }}</span></div></div>
        </div>
      </div>

      <div v-if="!isMcn" class="card detail-section-card">
        <div class="section-head">
          <div class="section-head__index">1</div>
          <div class="section-head__content"><h3>各平台账号情况</h3></div>
        </div>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>平台</th>
                <th>主播昵称</th>
                <th>粉丝数量（万）</th>
                <th>垂类赛道</th>
                <th>平台ID号</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in personalPlatformRows" :key="index">
                <td>{{ row.platform }}</td>
                <td>{{ row.nickname }}</td>
                <td>{{ row.fansCount }}</td>
                <td>{{ row.verticalTrack }}</td>
                <td>{{ row.platformId }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="isMcn" class="card detail-section-card">
        <div class="section-head">
          <div class="section-head__index">1</div>
          <div class="section-head__content"><h3>资产基本信息</h3></div>
        </div>
        <div class="detail-panel mcn-basic-layout">
          <div class="mcn-basic-summary">
            <div v-for="field in mcnBasicSummaryFields" :key="field.label" class="mcn-basic-summary__item">
              <div class="ii-label">{{ field.label }}</div>
              <div v-if="field.tag" class="tag tag-blue">{{ field.value }}</div>
              <div v-else class="ii-value">{{ field.value }}</div>
            </div>
          </div>

          <div class="mcn-basic-panel">
            <div class="mcn-basic-panel__title">权属人股权及融资情况</div>
            <div class="info-grid detail-panel__grid detail-panel__grid--mcn-basic-split">
              <div v-for="field in mcnOwnershipFinanceFields" :key="field.label" class="info-item" :class="field.fullWidth ? 'full-width' : ''">
                <span class="ii-label">{{ field.label }}</span>
                <span class="ii-value">{{ field.value }}</span>
              </div>
            </div>
          </div>

          <div class="mcn-basic-panel">
            <div class="mcn-basic-panel__title">机构基本情况</div>
            <div class="info-grid detail-panel__grid detail-panel__grid--mcn-basic-split">
              <div class="info-item">
                <span class="ii-label">成立时间</span>
                <span class="ii-value">{{ mcnBasicEstablishedAt }}</span>
              </div>
              <div class="info-item">
                <span class="ii-label">机构规模</span>
                <span class="ii-value">{{ mcnInstitutionScaleText }}</span>
              </div>
              <div class="info-item full-width">
                <span class="ii-label">主营业务范围</span>
                <div class="tag-line__content"><span v-for="item in mcnBusinessScopes" :key="item" class="tag tag-blue">{{ item }}</span></div>
              </div>
              <div class="info-item full-width">
                <span class="ii-label">核心合作平台</span>
                <div class="tag-line__content"><span v-for="item in mcnCorePlatforms" :key="item" class="tag tag-green">{{ item }}</span></div>
              </div>
              <div v-if="mcnQualifications.length" class="info-item full-width">
                <span class="ii-label">经营资质</span>
                <div class="tag-line__content"><span v-for="item in mcnQualifications" :key="item" class="tag tag-orange">{{ item }}</span></div>
              </div>
              <div class="info-item full-width">
                <span class="ii-label">机构办公地</span>
                <span class="ii-value">{{ mcnOfficeLocation }}</span>
              </div>
              <div v-if="mcnHonors.length" class="info-item full-width">
                <span class="ii-label" style="width: 100px;">行业荣誉/认证</span>
                <div class="tag-line__content"><span v-for="item in mcnHonors" :key="item" class="tag tag-purple">{{ item }}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isMcn" class="card detail-section-card">
        <div class="section-head">
          <div class="section-head__index">2</div>
          <div class="section-head__content"><h3>主播孵化数据情况</h3></div>
        </div>
        <div class="detail-panel">
          <div class="info-grid detail-panel__grid detail-panel__grid--mcn">
            <div v-for="field in mcnIncubationFields" :key="field.label" class="info-item" :class="field.fullWidth ? 'full-width' : ''">
              <span class="ii-label">{{ field.label }}</span>
              <span class="ii-value">{{ field.value }}</span>
            </div>
          </div>
          <div class="tag-line"><span class="tag-line__label">主播覆盖垂类</span><div class="tag-line__content"><span v-for="item in mcnIncubationVerticals" :key="item" class="tag tag-green">{{ item }}</span></div></div>
        </div>
      </div>

      <div class="card detail-section-card">
        <div class="section-head">
          <div class="section-head__index">{{ isMcn ? 3 : 2 }}</div>
          <div class="section-head__content"><h3>{{ isMcn ? '主播内容与影响力价值分析与商业转化能力评估' : '主播内容与影响力评估' }}</h3></div>
        </div>

        <div v-if="isMcn" class="valuation-entry-card">
          <div class="valuation-entry-card__section">
            <div class="valuation-entry-card__section-title">主播资产综合估值（用户提交）</div>
            <div class="valuation-entry-card__submitted-value">
              <span>主播资产综合估值（万元）</span>
              <strong>{{ mcnSubmittedValuationDisplay }}</strong>
            </div>
            <div class="valuation-entry-card__text-field">
              <span>估值说明</span>
              <p>{{ mcnValuationNote || '—' }}</p>
            </div>
          </div>

          <div class="valuation-entry-card__section">
            <div class="valuation-entry-card__section-title">内容创作与粉丝质量数据</div>
            <div class="valuation-entry-card__metrics">
              <div v-for="field in mcnContentInfluenceMetricFields" :key="field.label" class="valuation-entry-card__metric">
                <span>{{ field.label }}</span>
                <strong>{{ field.value }}</strong>
              </div>
            </div>
          </div>

          <div class="valuation-entry-card__section">
            <div class="valuation-entry-card__section-title">合规与风控体系说明</div>
            <div v-for="field in mcnContentInfluenceComplianceFields" :key="field.label" class="valuation-entry-card__text-field">
              <span>{{ field.label }}</span>
              <p>{{ field.value }}</p>
            </div>
          </div>

          <div class="valuation-entry-card__header valuation-entry-card__header--row">
            <div>
              <div class="valuation-entry-card__label">主播资产综合估值（管理员评估）</div>
              <div v-if="!isEditingValuation" class="valuation-entry-card__value">{{ mcnValuationDisplay }}</div>
            </div>
            <div v-if="isPending" class="valuation-entry-card__actions">
              <button v-if="!isEditingValuation" class="btn btn-outline btn-sm" type="button" @click="startEditValuation">修改</button>
              <template v-else>
                <button class="btn btn-outline btn-sm" type="button" @click="cancelEditValuation">取消</button>
                <button class="btn btn-primary btn-sm" type="button" @click="finishEditValuation">完成</button>
              </template>
            </div>
          </div>
          <div v-if="isEditingValuation" class="input-affix valuation-entry-card__control">
            <input v-model="reviewForm.streamer_asset_valuation" :disabled="!isPending" type="text" placeholder="请输入主播资产综合评估">
            <span class="input-affix__unit">万元</span>
          </div>
          <!-- {{ mcnValuationNote }} -->
          <div v-if="mcnValuationNote" class="valuation-entry-card__note">基于主播矩阵价值(例如：头部2人x800万+腰部8人x300万+成长型22人x50万)加平台账号价值及品牌溢价综合评估</div>
        </div>

        <div class="review-subsection">
          <div class="review-subsection__title">{{ isMcn ? '主播内容与影响力价值分析' : '（一）主播内容质量与合规评估' }}</div>
          <div v-for="group in primaryReviewGroups" :key="group.key" class="review-group-card">
            <div class="table-wrap">
              <table class="review-score-table">
                <thead>
                  <tr>
                    <th>评估维度</th>
                    <th>评估标准说明</th>
                    <th class="review-score-table__score">评分（1-10分）</th>
                    <th>评语</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in group.rows" :key="group.key + '-' + row.key" :class="row.key === 'overall_evaluation' ? 'review-score-table__row--summary' : ''">
                    <td :class="['review-score-table__dimension', row.key === 'overall_evaluation' ? 'review-score-table__dimension--summary' : '']">{{ row.label }}</td>
                    <td class="review-score-table__description">{{ row.description }}</td>
                    <td><input v-model.number="reviewForm[group.key][row.key].score" :disabled="!isPending" class="score-input" type="number" min="1" max="10"></td>
                    <td v-if="row.key === 'overall_evaluation'"><textarea v-model="reviewForm[group.key][row.key].comment" :disabled="!isPending" maxlength="200" rows="2" placeholder=" 总体评语:例如一-运营规范，内容稳定，粉丝真实有效，商业化路径清晰，整体水平为行业上游。"></textarea></td>
                    <td v-else><textarea v-model="reviewForm[group.key][row.key].comment" :disabled="!isPending" maxlength="200" rows="2" placeholder="评语..."></textarea></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="review-subsection">
          <div class="review-subsection__title">{{ isMcn ? '市场与商业潜力评估' : '（二）市场与商业能力评估' }}</div>
          <div v-for="group in secondaryReviewGroups" :key="group.key" class="review-group-card">
            <div class="review-group-card__subhead">{{ group.title }}</div>
            <div class="table-wrap">
              <table class="review-score-table">
                <thead>
                  <tr>
                    <th>评估维度</th>
                    <th>评估标准说明</th>
                    <th class="review-score-table__score">评分（1-10分）</th>
                    <th>评语</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in group.rows" :key="group.key + '-' + row.key">
                    <td class="review-score-table__dimension">{{ row.label }}</td>
                    <td class="review-score-table__description">{{ row.description }}</td>
                    <td><input v-model.number="reviewForm[group.key][row.key].score" :disabled="!isPending" class="score-input" type="number" min="1" max="10"></td>
                    <td><textarea v-model="reviewForm[group.key][row.key].comment" :disabled="!isPending" maxlength="200" rows="2" placeholder="评语..."></textarea></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-if="!isMcn" class="review-score-total">
            <span>市场与商业能力综合评分</span>
            <input v-model.number="reviewForm.market_commercial_score" :disabled="!isPending" class="score-input score-input--wide" type="number" min="1" max="10">
            <span>/10</span>
          </div>
        </div>
      </div>

      <div class="card detail-section-card">
        <div class="section-head">
          <div class="section-head__index">{{ isMcn ? 4 : 3 }}</div>
          <div class="section-head__content"><h3>主播账号资产与权益证明材料</h3></div>
        </div>
        <div class="detail-panel">
          <div v-if="isMcn" class="tag-line"><span class="tag-line__label zhzt" >账号归属主体</span><div class="tag-line__content"><span v-for="item in mcnOwnershipTypes" :key="item" class="tag tag-blue">{{ item }}</span></div></div>
          <div v-if="accountFileGroups.length" class="file-grid">
            <div v-for="group in accountFileGroups" :key="group.label" class="file-grid__item">
              <div class="cb-label">{{ group.label }}</div>
              <div class="file-list"><button v-for="item in group.items" :key="group.label + item.name + item.url" type="button" class="file-chip file-chip--link" @click="openMediaPreview(item)">{{ item.name }}</button></div>
            </div>
          </div>
          <div v-if="!accountFileGroups.length" class="empty-placeholder empty-placeholder--compact">暂无证明材料</div>
          <div class="form-group form-group--conclusion form-group--conclusion-block full-width">
            <label>账号资产与权益评估结论</label>
            <textarea v-model="reviewForm.account_asset_rights_conclusion" :disabled="!isPending" rows="4" placeholder="请审核员对主播账号资产与权益情况进行评估并填写结论......"></textarea>
          </div>
        </div>
      </div>

      <div class="card detail-section-card">
        <div class="section-head">
          <div class="section-head__index">{{ isMcn ? 5 : 4 }}</div>
          <div class="section-head__content"><h3>主播运营数据情况</h3></div>
        </div>
        <div v-if="!isMcn" class="detail-panel">
          <div class="operation-subsection">
            <div class="operation-subsection__title">一、账号核心运营指标</div>
            <div class="form-grid form-grid--readonly">
              <div v-for="field in personalOperationFields" :key="field.label" class="form-group" :class="field.fullWidth ? 'full-width' : ''">
                <label>{{ field.label }}</label>
                <div class="readonly-box">{{ field.value }}</div>
              </div>
            </div>
          </div>
          <div class="forecast-panel">
            <div class="forecast-panel__title">二、收入预测-综合多平台数据测算-用户提交</div>
            <div class="forecast-panel__grid">
              <div class="forecast-item"><span>短期收益</span><strong>{{ personalRevenueForecast.shortTerm }}</strong></div>
              <div class="forecast-item"><span>全生命周期收益</span><strong>{{ personalRevenueForecast.lifecycle }}</strong></div>
            </div>
            <div class="content-block forecast-panel__basis"><div class="cb-label">收益测算依据</div><div class="cb-value">{{ personalRevenueForecast.basis }}</div></div>
          </div>
        </div>
        <div v-else class="detail-panel">
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>平台ID</th>
                  <th>主播名称</th>
                  <th>全网粉丝（万）</th>
                  <th>点击量（万）</th>
                  <th>近一年直播场次</th>
                  <th>近一年利润（万元）</th>
                  <th>场均观看（万）</th>
                  <th>粉丝增长率（%）</th>
                  <th>直播转化率（%）</th>
                  <th>场均GMV（万）</th>
                  <th>客单价（元）</th>
                  <th>近12个月GMV（万）</th>
                  <th>垂直赛道</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in mcnStreamerRows" :key="index">
                  <td>{{ row.platformId }}</td>
                  <td>{{ row.name }}</td>
                  <td>{{ row.totalFans }}</td>
                  <td>{{ row.totalClicks }}</td>
                  <td>{{ row.lastYearLiveSessions }}</td>
                  <td>{{ row.lastYearProfit }}</td>
                  <td>{{ row.avgViewers }}</td>
                  <td>{{ row.fanGrowthRate }}</td>
                  <td>{{ row.liveConversionRate }}</td>
                  <td>{{ row.avgGmv }}</td>
                  <td>{{ row.avgOrderValue }}</td>
                  <td>{{ row.last12mGmv }}</td>
                  <td>{{ row.verticalTrack }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="operation-subsection mcn-operation-subsection">
            <div class="operation-subsection__title">机构经营概况</div>
            <div class="info-grid detail-panel__grid detail-panel__grid--operation">
              <div v-for="field in mcnOperationSummaryFields" :key="field.label" class="info-item" :class="field.fullWidth ? 'full-width' : ''">
                <span class="ii-label">{{ field.label }}</span>
                <span class="ii-value">{{ field.value }}</span>
              </div>
            </div>
          </div>
          <div class="operation-subsection mcn-operation-subsection">
            <div class="operation-subsection__title">收入结构及成本占比分析</div>
            <div class="info-grid detail-panel__grid detail-panel__grid--operation">
              <div v-for="field in mcnIncomeRatioFields" :key="field.label" class="info-item" :class="field.fullWidth ? 'full-width' : ''">
                <span class="ii-label">{{ field.label }}</span>
                <span class="ii-value">{{ field.value }}</span>
              </div>
            </div>
          </div>
          <div class="forecast-panel forecast-panel--mcn">
            <div class="forecast-panel__title">收益预测-综合多平台数据测算-用户提交</div>
            <div class="forecast-panel__grid">
              <div v-for="field in mcnRevenueForecastCards" :key="field.label" class="forecast-item">
                <span>{{ field.label }}</span>
                <strong>{{ field.value }}</strong>
              </div>
            </div>
            <div class="content-block forecast-panel__basis"><div class="cb-label">收益测算依据</div><div class="cb-value">{{ mcnRevenueBasisText }}</div></div>
          </div>
          <div class="operation-subsection mcn-operation-subsection">
            <div class="operation-subsection__title">第三方支撑与生态协作</div>
            <div class="readonly-box">{{ mcnThirdPartySupportText }}</div>
          </div>
        </div>
      </div>

      <div class="card detail-section-card">
        <div class="section-head">
          <div class="section-head__index">{{ isMcn ? 6 : 5 }}</div>
          <div class="section-head__content"><h3>{{ isMcn ? '商业化评估' : '商业化情况评估与终审' }}</h3></div>
        </div>
        <div v-if="isMcn" class="forecast-panel forecast-panel--mcn-submitted">
          <div class="forecast-panel__title">MCN机构预计收益（用户提交）</div>
          <div class="forecast-panel__grid">
            <div v-for="field in mcnCommercialForecastCards" :key="field.label" class="forecast-item">
              <span>{{ field.label }}</span>
              <strong>{{ field.value }}</strong>
            </div>
          </div>
        </div>
        <div class="review-conclusion-card review-conclusion-card--final">
          <div class="review-conclusion-card__title">收入预测——管理员评估</div>
          <div class="review-conclusion-grid review-conclusion-grid--final">
            <div class="form-group">
              <label>{{ isMcn ? '短期收益-未来12个月' : '短期收入预测' }}</label>
              <div class="input-affix"><input v-model="reviewForm.short_term_income_forecast" :disabled="!isPending" type="text" placeholder="请输入短期预测"><span class="input-affix__unit">万元</span></div>
            </div>
            <div class="form-group">
              <label>{{ isMcn ? '全生命周期收益-5年' : '全生命周期收入预测' }}</label>
              <div class="input-affix"><input v-model="reviewForm.lifecycle_income_forecast" :disabled="!isPending" type="text" placeholder="请输入全生命周期预测"><span class="input-affix__unit">万元</span></div>
            </div>
            <div class="form-group full-width">
              <label>综合评估意见</label>
              <textarea v-model="reviewForm.overall_opinion" :disabled="!isPending" rows="5" placeholder="请输入综合评估意见"></textarea>
            </div>
            <div v-if="currentItem.status === 'rejected'" class="form-group full-width">
              <label>驳回原因</label>
              <div class="result-inline result-inline--danger">{{ currentItem.rejectedReason || '—' }}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isPending" class="action-bar evaluation-action-bar">
        <div class="action-bar__btns action-bar__btns--right">
          <button class="btn btn-outline" type="button" :disabled="submittingDraft || submittingProcess" @click="saveDraft">{{ submittingDraft ? '保存中...' : '保存' }}</button>
          <button class="btn btn-danger" type="button" :disabled="submittingDraft || submittingProcess" @click="submitRejected">
            <span v-if="processingAction === 'rejected'" class="btn-loading-spinner" aria-hidden="true"></span>
            <span>{{ processingAction === 'rejected' ? '提交中...' : '评估不通过' }}</span>
          </button>
          <button class="btn btn-success" type="button" :disabled="submittingDraft || submittingProcess" @click="submitApproved">
            <span v-if="processingAction === 'approved'" class="btn-loading-spinner" aria-hidden="true"></span>
            <span>{{ processingAction === 'approved' ? '提交中...' : '审核通过，提交申请' }}</span>
          </button>
        </div>
      </div>
    </template>

    <el-dialog :title="previewTitle" :visible.sync="previewDialogVisible" width="960px" top="6vh">
      <div v-if="previewUrl" class="media-preview">
        <img v-if="previewType === 'image'" class="media-preview__image" :src="previewUrl" :alt="previewTitle" referrerpolicy="no-referrer">
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  assetEvaluationDetail,
  assetEvaluationProcess,
  assetEvaluationReviewDraft
} from '../../../api/userMG'
import { downloadRemoteFile } from '../../../utils/download'

const FIELD_LABELS = {
  name: '姓名',
  nickname: '昵称',
  real_name: '真实姓名',
  phone: '手机号',
  email: '邮箱',
  company: '公司名称',
  company_name: '公司名称',
  company_full: '机构全称',
  owner: '资产权属人',
  anchor_name: '主播名称',
  anchor_type: '主播类型',
  content_directions: '主营内容方向',
  content_direction: '主营内容方向',
  platforms: '合作平台',
  honors: '行业荣誉 / 认证',
  platform_accounts: '平台账号情况',
  platform_account_list: '平台账号情况',
  account_owner: '账号权属信息',
  ownership_files: '权属证明材料',
  usage_files: '使用权证明材料',
  income_files: '收益证明文件',
  contract_files: '商业合约 / 授权文件',
  rights: '账号资产与权益',
  rights_info: '账号资产与权益',
  metrics: '账号运营指标',
  income_forecast: '收入预测',
  current_year: '本年度收入预测',
  next_year: '下一年度收入预测',
  basis: '预测依据',
  reg_capital: '注册资本',
  paid_capital: '实缴资本',
  equity_structure: '股权结构',
  financing_history: '融资历史',
  subsidiaries: '关联主体',
  establish_date: '成立时间',
  scale: '机构规模',
  business_scope: '主营业务',
  qualifications: '经营资质',
  office: '机构办公地',
  incubation: '主播孵化数据情况',
  total: '签约主播总数',
  exclusive: '独家签约主播',
  contract_mix: '全约 / 商务约',
  tiers: '主播梯队结构',
  verticals: '覆盖垂类',
  avg_cost: '平均孵化成本',
  annual_cost: '年度孵化总成本',
  process: '孵化周期与过程',
  operation_rows: '主播运营数据情况',
  income_structure: '收入结构',
  commission: '佣金收入占比',
  live: '直播收入占比',
  brand: '品牌营销收入占比',
  other: '其他收入占比',
  forecast_basis: '收益测算依据',
  evaluate_no: '评估编号',
  asset_name: '资产名称',
  submitted_at: '提交时间',
  created_at: '创建时间',
  handled_at: '处理时间',
  review_drafted_at: '草稿保存时间',
  report_url: '报告链接',
  reject_reason: '驳回原因',
  values_compliance: '价值观与合规性',
  persona_positioning_recognition: '人设定位与辨识度',
  content_creation: '内容创作能力',
  fan_quality_activity: '粉丝质量与活跃度',
  overall_evaluation: '总体评价',
  compliance_risk_awareness: '合规风控意识',
  compliance_risk_system: '合规风控体系',
  fan_growth_efficiency: '粉丝增长效率',
  fan_stickiness: '粉丝黏性',
  cross_platform_expansion: '跨平台拓展',
  live_conversion_rate: '直播转化率',
  brand_premium: '品牌溢价',
  supply_chain_resources: '供应链资源',
  supply_chain_depth: '供应链纵深',
  content_breakout_rate: '内容出圈率',
  social_spread: '社交传播',
  cross_industry_collaboration: '跨界合作',
  streamer_ip: '主播IP化',
  team_potential: '团队化潜力',
  model_sustainability: '模式可持续性',
  newcomer_incubation: '新人孵化',
  model_replicability: '模式可复制',
  market_commercial_score: '市场与商业潜力综合评分',
  streamer_asset_valuation: '主播资产综合评估',
  account_asset_rights_conclusion: '账号资产与权益评估结论',
  short_term_income_forecast: '短期收入预测',
  lifecycle_income_forecast: '全生命周期收入预测',
  overall_opinion: '综合评估意见',
  score: '评分',
  comment: '评语',
  platform: '平台',
  nickname_text: '主播昵称',
  fans: '粉丝数量',
  vertical: '垂类赛道',
  account_id: '账号ID',
  clicks: '点击量',
  sessions: '直播场次',
  profit: '利润',
  avg_view: '场均观看',
  growth: '增长率',
  convert: '转化率',
  avg_gmv: '场均GMV',
  unit_price: '客单价',
  annual_gmv: '年GMV'
  ,asset_category: '资产类别'
  ,owner_name: '资产权属人'
  ,owner_id_card: '权属人身份证号'
  ,residence: '常住所在地'
  ,streamer_type_text: '主播类型'
  ,platforms: '平台账号'
  ,platform_id: '平台ID'
  ,fans_count: '粉丝数量'
  ,operation: '主播运营情况'
  ,total_fans: '全网粉丝总数'
  ,works_stats: '全网作品总播放量与作品数量'
  ,live_sessions_stats: '近一年直播场次与场均观看人数'
  ,monthly_live_hours: '月均直播时长'
  ,fan_growth: '粉丝增长情况'
  ,commercial_cooperation: '近一年商业合作情况'
  ,annual_income: '近一年收入情况'
  ,evidence_files: '证明材料'
  ,revenue_forecast: '收入预测'
  ,short_term: '短期收益'
  ,lifecycle: '全生命周期收益'
  ,basic: '基本信息'
  ,credit_code: '统一社会信用代码'
  ,registered_capital: '注册资本'
  ,paid_in_capital: '实缴资本'
  ,affiliates: '控股子公司/关联公司'
  ,established_at: '成立时间'
  ,employee_count: '全职员工人数'
  ,signed_streamer_count: '签约主播人数'
  ,core_platforms: '核心合作平台'
  ,office_location: '机构办公地'
  ,content_influence: '内容影响力'
  ,signed_streamer_total: '签约主播总数'
  ,exclusive_streamer_count: '独家签约主播'
  ,full_contract_count: '全约人数'
  ,business_contract_count: '商务约人数'
  ,top_streamer_count: '头部主播人数'
  ,waist_streamer_count: '腰部主播人数'
  ,growth_streamer_count: '成长型主播人数'
  ,process_description: '孵化周期与过程描述'
  ,avg_incubation_cost: '单主播平均孵化成本'
  ,annual_incubation_investment: '年度孵化总投入'
  ,asset_valuation: '主播资产综合评估'
  ,valuation_note: '估值说明'
  ,short_video_monthly_updates: '短视频月均更新量'
  ,short_video_hit_rate: '短视频爆款率'
  ,avg_live_stay_duration: '直播间平均停留时长'
  ,fan_interaction_rate: '粉丝互动率'
  ,live_interaction_rate: '直播互动率'
  ,cross_platform_account_count: '跨平台账号总数'
  ,selection_review_process: '选品审核流程说明'
  ,compliance_mechanism: '内容合规机制'
  ,account_assets: '账号资产'
  ,ownership_types: '账号归属主体'
  ,ownership_auth_files: '账号归属主体授权文件'
  ,use_right_auth_files: '使用权授权文件'
  ,revenue_right_auth_files: '收益权授权文件'
  ,copyright_files: '作品著作权与内容资产处置权证明文件'
  ,brand_cooperation_files: '商业合作与品牌合作证明文件'
  ,streamers: '主播数据'
  ,total_clicks: '全网作品点击量'
  ,last_year_live_sessions: '近一年直播场次'
  ,last_year_profit: '近一年利润'
  ,avg_viewers: '场均观看人数'
  ,fan_growth_rate: '粉丝增长率'
  ,avg_order_value: '客单价'
  ,last_12m_gmv: '近12个月GMV'
  ,annual_revenue: '机构年度总营收'
  ,annual_net_profit: '机构年度净利润'
  ,last_12m_live_sessions: '近12个月直播场次'
  ,total_fan_coverage: '总粉丝覆盖量'
  ,commission_income_ratio: '佣金收入占比'
  ,slot_fee_income_ratio: '坑位费收入占比'
  ,brand_marketing_income_ratio: '品牌营销收入占比'
  ,operating_cost_ratio: '运营成本率'
  ,net_profit_ratio: '净利润率'
  ,short_term_6m: '短期收益（未来6个月）'
  ,lifecycle_3y: '全生命周期收益（3年）'
  ,revenue_basis: '收益测算依据'
  ,third_party_support: '第三方支撑与生态协作'
  ,commercial: '商业化评估'
}

const ENUM_LABELS = {
  user_type: {
    individual: '个人',
    mcn: 'MCN机构'
  },
  role_type: {
    individual: '个人',
    organization: '机构'
  },
  asset_evaluation_status: {
    draft: '草稿',
    pending: '待审核',
    approved: '已通过',
    rejected: '已驳回'
  },
  streamer_type: {
    full_time: '全职',
    part_time: '兼职'
  },
  content_direction: {
    live_commerce: '直播带货',
    content: '内容创作',
    knowledge: '知识付费',
    entertainment: '娱乐直播'
  },
  platform: {
    douyin: '抖音',
    kuaishou: '快手',
    bilibili: 'B站',
    xiaohongshu: '小红书',
    weibo: '微博',
    wechat_video: '微信视频号',
    taobao_live: '淘宝直播',
    youtube: 'YouTube',
    tiktok: 'TikTok'
  },
  vertical_track: {
    beauty_care: '美妆护肤',
    fashion_apparel: '服饰穿搭',
    food_beverage: '食品饮料',
    digital: '3C数码',
    home_living: '家居生活',
    maternity_child: '母婴育儿',
    education: '教育知识',
    sports_outdoors: '运动户外',
    gaming: '游戏电竞'
  },
  business_scope: {
    live_commerce: '直播带货',
    content: '内容创作',
    artist_brokerage: '艺人经纪',
    brand_operation: '品牌代运营',
    knowledge: '知识付费',
    ip_authorization: 'IP授权'
  },
  qualification: {
    performance_license: '营业性演出许可证',
    network_culture_license: '网络文化经营许可证',
    icp_license: 'ICP许可证',
    radio_tv_production: '广播电视节目制作'
  },
  account_ownership_type: {
    mcn_owned: 'MCN机构名下',
    streamer_authorized: '主播个人名下（有授权）',
    jointly_owned: 'MCN与主播共有',
    affiliate_owned: '关联公司名下'
  }
}

const KEY_ENUM_MAP = {
  user_type: 'user_type',
  role_type: 'role_type',
  status: 'asset_evaluation_status',
  streamer_type: 'streamer_type',
  content_direction: 'content_direction',
  platform: 'platform',
  core_platforms: 'platform',
  vertical_track: 'vertical_track',
  verticals: 'vertical_track',
  business_scope: 'business_scope',
  qualifications: 'qualification',
  ownership_types: 'account_ownership_type'
}

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]'
}

function createScoreItem() {
  return {
    score: '',
    comment: ''
  }
}

const FIELD_UNIT_MAP = {
  registered_capital: '万元',
  paid_in_capital: '万元',
  avg_incubation_cost: '万元',
  annual_incubation_investment: '万元',
  annual_gmv: '万元',
  annual_revenue: '万元',
  annual_net_profit: '万元',
  short_term_6m: '万元',
  lifecycle_3y: '万元',
  employee_count: '人',
  signed_streamer_count: '人',
  signed_streamer_total: '人',
  exclusive_streamer_count: '人',
  full_contract_count: '人',
  business_contract_count: '人',
  top_streamer_count: '人',
  waist_streamer_count: '人',
  growth_streamer_count: '人',
  fan_growth_rate: '%',
  live_conversion_rate: '%',
  commission_income_ratio: '%',
  slot_fee_income_ratio: '%',
  brand_marketing_income_ratio: '%',
  other_income_ratio: '%',
  operating_cost_ratio: '%',
  net_profit_ratio: '%'
}

const AFFIX_INPUT_FIELDS = {
  streamer_asset_valuation: '万元',
  short_term_income_forecast: '万元',
  lifecycle_income_forecast: '万元'
}

function getReviewBlueprint(userType) {
  const isMcn = userType === 'mcn'

  return {
    groups: [
      {
        key: 'content_quality_compliance',
        title: '内容质量与合规评估',
        description: isMcn ? '从价值观合规、人设辨识、内容生产和机构风控体系评估内容基础。' : '从价值观合规、人设辨识、内容生产与个人风控意识评估内容基础。',
        rows: [
          { key: 'values_compliance', label: isMcn ? '价值观合规性' : '价值观与合规性', description: '内容价值导向、表达方式与平台合规表现。' },
          { key: 'persona_positioning_recognition', label: '人设定位与辨识度', description: '主播定位清晰度、记忆点和受众识别度。' },
          { key: 'content_creation', label: '内容创作能力', description: '选题、脚本、表达和持续输出能力。' },
          { key: 'fan_quality_activity', label: '粉丝质量与活跃度', description: '粉丝画像匹配度、互动表现与活跃情况。' },
          { key: isMcn ? 'compliance_risk_system' : 'compliance_risk_awareness', label: isMcn ? '合规风控体系' : '合规风控意识', description: isMcn ? '机构风控流程、审核机制与异常响应能力。' : '个人对内容、选品和交易风险的识别与处理能力。' },
          { key: 'overall_evaluation', label: '总体评价', description: '对本评分组的综合判断。' }
        ]
      },
      {
        key: 'growth_retention',
        title: '用户增长与留存潜力',
        description: '从增长效率、粘性和跨平台延展评估持续成长空间。',
        rows: [
          { key: 'fan_growth_efficiency', label: '粉丝增长效率', description: '自然流增长、投放效率与新增转化质量。' },
          { key: 'fan_stickiness', label: '粉丝黏性', description: '复访、停留、复购与社群活跃表现。' },
          { key: 'cross_platform_expansion', label: '跨平台拓展', description: '多平台经营、内容迁移和粉丝承接能力。' }
        ]
      },
      {
        key: 'monetization_roi',
        title: '变现效率与ROI空间',
        description: isMcn ? '围绕转化、品牌溢价与供应链纵深评估商业效率。' : '围绕转化、品牌溢价与供应链资源评估商业效率。',
        rows: [
          { key: 'live_conversion_rate', label: '直播转化率', description: '直播成交、转化链路和成交稳定性。' },
          { key: 'brand_premium', label: '品牌溢价', description: '商业报价能力和品牌合作议价空间。' },
          { key: isMcn ? 'supply_chain_depth' : 'supply_chain_resources', label: isMcn ? '供应链纵深' : '供应链资源', description: isMcn ? '机构对供应链、品类和合作资源的深度掌控。' : '个人主播在货盘、资源与履约侧的获取能力。' }
        ]
      },
      {
        key: 'content_virality',
        title: '内容裂变与话题潜力',
        description: '评估内容破圈传播和跨圈层协同潜力。',
        rows: [
          { key: 'content_breakout_rate', label: '内容出圈率', description: '内容破圈能力和爆款稳定性。' },
          { key: 'social_spread', label: '社交传播', description: '站内外转发、讨论和二次传播表现。' },
          { key: 'cross_industry_collaboration', label: '跨界合作', description: '与其他行业、品牌或 IP 的联动空间。' }
        ]
      },
      {
        key: isMcn ? 'ip_matrix' : 'ip_extension',
        title: 'IP延展与发展潜力',
        description: isMcn ? '从主播IP、新人孵化与模式复制评估长期发展潜力。' : '从主播IP、团队化潜力和模式可持续性评估发展潜力。',
        rows: isMcn
          ? [
            { key: 'streamer_ip', label: '主播IP化', description: '主播品牌化、人格化和长期可运营能力。' },
            { key: 'newcomer_incubation', label: '新人孵化', description: '机构培养新主播与梯队接续能力。' },
            { key: 'model_replicability', label: '模式可复制', description: '方法论沉淀、复制路径和规模化能力。' }
          ]
          : [
            { key: 'streamer_ip', label: '主播IP化', description: '主播品牌化、人格化和长期可运营能力。' },
            { key: 'team_potential', label: '团队化潜力', description: '组织协同、团队建设和分工扩展能力。' },
            { key: 'model_sustainability', label: '模式可持续性', description: '内容和变现模型的长期稳定性。' }
          ]
      }
    ],
    standaloneFields: isMcn
      ? [
        { key: 'streamer_asset_valuation', label: '主播资产综合评估', type: 'text', maxlength: 255, placeholder: '请输入主播资产综合评估' }
      ]
      : [
        { key: 'market_commercial_score', label: '市场与商业潜力综合评分', type: 'score', placeholder: '1-10' }
      ],
    conclusionFields: [
      { key: 'account_asset_rights_conclusion', label: '账号资产与权益评估结论', multiline: true, rows: 4, maxlength: 2000, placeholder: '请审核员对主播账号资产与权益情况进行评估并填写结论......' },
      { key: 'short_term_income_forecast', label: '短期收入预测', multiline: false, maxlength: 255, placeholder: '请输入短期收入预测' },
      { key: 'lifecycle_income_forecast', label: '全生命周期收入预测', multiline: false, maxlength: 255, placeholder: '请输入全生命周期收入预测' },
      { key: 'overall_opinion', label: '综合评估意见', multiline: true, rows: 5, maxlength: 2000, placeholder: '请输入综合评估意见' }
    ]
  }
}

function buildEmptyReviewForm(userType) {
  const blueprint = getReviewBlueprint(userType)
  const result = {}

  blueprint.groups.forEach(group => {
    result[group.key] = {}
    group.rows.forEach(row => {
      result[group.key][row.key] = createScoreItem()
    })
  })

  blueprint.standaloneFields.forEach(field => {
    result[field.key] = ''
  })

  blueprint.conclusionFields.forEach(field => {
    result[field.key] = ''
  })

  return result
}

function normalizeScoreInput(value) {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  const score = Number(value)
  return isFinite(score) ? score : ''
}

function mergeReviewForm(userType, source) {
  const blueprint = getReviewBlueprint(userType)
  const result = buildEmptyReviewForm(userType)
  const extra = isPlainObject(source) ? source : {}

  blueprint.groups.forEach(group => {
    const groupValue = isPlainObject(extra[group.key]) ? extra[group.key] : {}

    group.rows.forEach(row => {
      const rowValue = isPlainObject(groupValue[row.key]) ? groupValue[row.key] : {}
      result[group.key][row.key] = {
        score: normalizeScoreInput(rowValue.score),
        comment: rowValue.comment || ''
      }
    })
  })

  blueprint.standaloneFields.forEach(field => {
    if (field.type === 'score') {
      result[field.key] = normalizeScoreInput(extra[field.key])
      return
    }

    result[field.key] = normalizeAffixInputValue(extra[field.key], AFFIX_INPUT_FIELDS[field.key])
  })

  blueprint.conclusionFields.forEach(field => {
    result[field.key] = normalizeAffixInputValue(extra[field.key], AFFIX_INPUT_FIELDS[field.key])
  })

  return result
}

function normalizeAffixInputValue(value, unit) {
  if (value === null || value === undefined) return ''

  const text = String(value).trim()

  if (!text || !unit) {
    return text
  }

  if (unit === '%') {
    return text.replace(/[\s]*(%|％)$/u, '')
  }

  const unitPattern = new RegExp('(?:\\s*)' + unit + '$')
  return text.replace(unitPattern, '')
}

export default {
  name: 'evaluation-application-detail',
  data() {
    return {
      loading: false,
      submittingDraft: false,
      submittingProcess: false,
      processingAction: '',
      currentItem: null,
      reviewForm: buildEmptyReviewForm('individual'),
      editingValuation: false,
      valuationDraftBackup: '',
      previewDialogVisible: false,
      previewTitle: '附件预览',
      previewType: 'image',
      previewUrl: ''
    }
  },
  computed: {
    isMcn() {
      return !!(this.currentItem && this.currentItem.userType === 'mcn')
    },
    isPending() {
      return !!(this.currentItem && this.currentItem.status === 'pending')
    },
    isApproved() {
      return !!(this.currentItem && this.currentItem.status === 'approved')
    },
    summaryFields() {
      const fields = [
        { label: '申请编号', value: this.currentItem ? this.currentItem.evaluateNo : '—' },
        { label: '申请人', value: this.currentItem ? this.currentItem.submitterName : '—' },
        { label: '联系电话', value: this.currentItem ? this.currentItem.submitterPhone : '—' },
        { label: '提交时间', value: this.currentItem ? this.currentItem.submittedAt : '—' }
      ]

      if (this.isApproved) {
        fields.push(
          { label: '存证hash', value: this.currentItem.blockchainHash || '—' },
          { label: '存证地址', value: this.currentItem.blockchainTxHash || '—' }
        )
      }

      return fields
    },
    personalData() {
      return this.currentItem ? (this.currentItem.individualData || {}) : {}
    },
    personalBasicFields() {
      const data = this.personalData
      return [
        this.buildField('资产名称', this.currentItem ? this.currentItem.assetName : ''),
        this.buildField('资产类别', this.currentItem ? this.currentItem.assetCategory : ''),
        this.buildField('资产权属人', data.owner_name, 'owner_name'),
        this.buildField('权属人身份证号', data.owner_id_card, 'owner_id_card'),
        this.buildField('常住所在地', data.residence, 'residence')
      ]
    },
    personalContentDirections() {
      return this.ensureDisplayArray(this.personalData.content_direction, 'content_direction')
    },
    personalHonorPlatforms() {
      return this.uniqueDisplayList(this.personalPlatformRows.map(item => item.platform))
    },
    personalCoreVerticals() {
      return this.uniqueDisplayList(this.personalPlatformRows.map(item => item.verticalTrack))
    },
    personalHonors() {
      return this.ensureDisplayArray(this.personalData.honors)
    },
    personalPlatformRows() {
      const list = Array.isArray(this.personalData.platforms) ? this.personalData.platforms : []
      return list.map(item => ({
        platform: this.formatDisplayValue(item.platform, 'platform'),
        nickname: this.formatDisplayValue(item.nickname),
        fansCount: this.formatDisplayValue(item.fans_count),
        verticalTrack: this.formatDisplayValue(item.vertical_track, 'vertical_track'),
        platformId: this.formatDisplayValue(item.platform_id)
      }))
    },
    personalOperationData() {
      return isPlainObject(this.personalData.operation) ? this.personalData.operation : {}
    },
    personalOperationFields() {
      const data = this.personalOperationData
      return [
        this.buildField('全网粉丝总数', data.total_fans, 'total_fans'),
        this.buildField('全网作品总播放量与作品数量', data.works_stats, 'works_stats', true),
        this.buildField('近一年直播场次与场均观看人数', data.live_sessions_stats, 'live_sessions_stats', true),
        this.buildField('月均直播时长', data.monthly_live_hours, 'monthly_live_hours'),
        this.buildField('粉丝增长情况', data.fan_growth, 'fan_growth', true),
        this.buildField('近一年商业合作情况', data.commercial_cooperation, 'commercial_cooperation', true),
        this.buildField('近一年收入情况', data.annual_income, 'annual_income', true)
      ]
    },
    personalRevenueForecast() {
      const data = isPlainObject(this.personalData.revenue_forecast) ? this.personalData.revenue_forecast : {}
      return {
        shortTerm: this.formatDisplayValue(data.short_term),
        lifecycle: this.formatDisplayValue(data.lifecycle),
        basis: this.formatDisplayValue(data.basis)
      }
    },
    mcnDataComputed() {
      return this.currentItem ? (this.currentItem.mcnData || {}) : {}
    },
    mcnBasicData() {
      return isPlainObject(this.mcnDataComputed.basic) ? this.mcnDataComputed.basic : {}
    },
    mcnAssetOwnerText() {
      const data = this.mcnBasicData
      const fallbackOwner = this.currentItem ? this.currentItem.submitterName : ''
      return this.formatDisplayValue(data.owner_name || data.owner || fallbackOwner, 'owner_name')
    },
    mcnCompanyCreditText() {
      const companyName = this.formatDisplayValue(this.mcnBasicData.company_name, 'company_name')
      const creditCode = this.formatDisplayValue(this.mcnBasicData.credit_code, 'credit_code')

      if (companyName === '—') return creditCode
      if (creditCode === '—') return companyName
      return companyName + '（' + creditCode + '）'
    },
    mcnBasicSummaryFields() {
      return [
        { label: '资产名称', value: this.currentItem ? this.currentItem.assetName : '—' },
        { label: '资产类别', value: this.currentItem ? this.currentItem.assetCategory : '—', tag: true },
        { label: '资产权属人', value: this.mcnAssetOwnerText },
        { label: '机构全称（统一社会信用代码）', value: this.mcnCompanyCreditText }
      ]
    },
    mcnOwnershipFinanceFields() {
      const data = this.mcnBasicData
      return [
        this.buildField('注册资本', data.registered_capital, 'registered_capital'),
        this.buildField('实缴资本', data.paid_in_capital, 'paid_in_capital'),
        this.buildField('股权结构（持股比例、主要股东及实际控制人）', data.equity_structure, 'equity_structure', true),
        this.buildField('融资历史（轮次、金额、投资方、估值）', data.financing_history, 'financing_history', true),
        this.buildField('控股子公司/关联公司（与主播业务相关的关联主体）', data.affiliates, 'affiliates', true)
      ]
    },
    mcnBasicEstablishedAt() {
      return this.formatDisplayValue(this.mcnBasicData.established_at, 'established_at')
    },
    mcnInstitutionScaleText() {
      const employeeCount = this.formatDisplayValue(this.mcnBasicData.employee_count, 'employee_count')
      const streamerCount = this.formatDisplayValue(this.mcnBasicData.signed_streamer_count, 'signed_streamer_count')

      if (employeeCount === '—' && streamerCount === '—') return '—'
      if (employeeCount === '—') return '签约主播 ' + streamerCount
      if (streamerCount === '—') return '全职员工 ' + employeeCount
      return '全职员工 ' + employeeCount + ' / 签约主播 ' + streamerCount
    },
    mcnOfficeLocation() {
      return this.formatDisplayValue(this.mcnBasicData.office_location, 'office_location')
    },
    mcnBasicFields() {
      const data = this.mcnBasicData
      return [
        this.buildField('资产名称', this.currentItem ? this.currentItem.assetName : '', 'asset_name'),
        this.buildField('资产类别', this.currentItem ? this.currentItem.assetCategory : '', 'asset_category'),
        this.buildField('机构全称', data.company_name, 'company_name'),
        this.buildField('统一社会信用代码', data.credit_code, 'credit_code'),
        this.buildField('注册资本', data.registered_capital, 'registered_capital'),
        this.buildField('实缴资本', data.paid_in_capital, 'paid_in_capital'),
        this.buildField('成立时间', data.established_at, 'established_at'),
        this.buildField('全职员工人数', data.employee_count, 'employee_count'),
        this.buildField('签约主播人数', data.signed_streamer_count, 'signed_streamer_count'),
        this.buildField('机构办公地', data.office_location, 'office_location', true),
        this.buildField('股权结构', data.equity_structure, 'equity_structure', true),
        this.buildField('融资历史', data.financing_history, 'financing_history', true),
        this.buildField('控股子公司/关联公司', data.affiliates, 'affiliates', true)
      ]
    },
    mcnBusinessScopes() {
      return this.ensureDisplayArray(this.mcnBasicData.business_scope, 'business_scope')
    },
    mcnCorePlatforms() {
      return this.ensureDisplayArray(this.mcnBasicData.core_platforms, 'core_platforms')
    },
    mcnQualifications() {
      return this.ensureDisplayArray(this.mcnBasicData.qualifications, 'qualifications')
    },
    mcnHonors() {
      return this.ensureDisplayArray(this.mcnBasicData.honors)
    },
    mcnIncubationData() {
      return isPlainObject(this.mcnDataComputed.incubation) ? this.mcnDataComputed.incubation : {}
    },
    mcnIncubationFields() {
      const data = this.mcnIncubationData
      return [
        this.buildField('签约主播总数', data.signed_streamer_total, 'signed_streamer_total'),
        this.buildField('独家签约主播', data.exclusive_streamer_count, 'exclusive_streamer_count'),
        this.buildField('全约人数', data.full_contract_count, 'full_contract_count'),
        this.buildField('商务约人数', data.business_contract_count, 'business_contract_count'),
        this.buildField('头部主播人数', data.top_streamer_count, 'top_streamer_count'),
        this.buildField('腰部主播人数', data.waist_streamer_count, 'waist_streamer_count'),
        this.buildField('成长型主播人数', data.growth_streamer_count, 'growth_streamer_count'),
        this.buildField('单主播平均孵化成本', data.avg_incubation_cost, 'avg_incubation_cost'),
        this.buildField('年度孵化总投入', data.annual_incubation_investment, 'annual_incubation_investment'),
        this.buildField('孵化周期与过程描述', data.process_description, 'process_description', true)
      ]
    },
    mcnIncubationVerticals() {
      return this.ensureDisplayArray(this.mcnIncubationData.verticals, 'verticals')
    },
    mcnContentInfluenceData() {
      return isPlainObject(this.mcnDataComputed.content_influence) ? this.mcnDataComputed.content_influence : {}
    },
    mcnValuationNote() {
      return this.mcnContentInfluenceData.valuation_note || ''
    },
    mcnSubmittedValuationDisplay() {
      return this.formatDisplayValue(this.mcnContentInfluenceData.asset_valuation, 'asset_valuation')
    },
    mcnContentInfluenceMetricFields() {
      const data = this.mcnContentInfluenceData
      return [
        this.buildField('短视频月均更新量', data.short_video_monthly_updates, 'short_video_monthly_updates'),
        this.buildField('短视频爆款率（播放>=100万）', data.short_video_hit_rate, 'short_video_hit_rate'),
        this.buildField('直播间平均停留时长（秒）', data.avg_live_stay_duration, 'avg_live_stay_duration'),
        this.buildField('粉丝互动率', data.fan_interaction_rate, 'fan_interaction_rate'),
        this.buildField('直播间互动率', data.live_interaction_rate, 'live_interaction_rate'),
        this.buildField('跨平台账号总数', data.cross_platform_account_count, 'cross_platform_account_count')
      ]
    },
    mcnContentInfluenceComplianceFields() {
      const data = this.mcnContentInfluenceData
      return [
        this.buildField('选品审核流程说明', data.selection_review_process, 'selection_review_process'),
        this.buildField('内容合规机制', data.compliance_mechanism, 'compliance_mechanism')
      ]
    },
    mcnValuationDisplay() {
      if (!this.reviewForm.streamer_asset_valuation) return '万元'
      return this.appendUnit(this.reviewForm.streamer_asset_valuation, '万元')
    },
    isEditingValuation() {
      return this.isPending && this.editingValuation
    },
    primaryReviewGroups() {
      return this.reviewGroups.slice(0, 1)
    },
    secondaryReviewGroups() {
      return this.reviewGroups.slice(1)
    },
    accountFileGroups() {
      if (!this.currentItem) return []

      if (!this.isMcn) {
        const operation = this.personalOperationData
        return this.createFileGroups([
          { label: '证明材料', value: operation.evidence_files }
        ])
      }

      const data = isPlainObject(this.mcnDataComputed.account_assets) ? this.mcnDataComputed.account_assets : {}
      return this.createFileGroups([
        { label: '账号归属主体授权文件', value: data.ownership_auth_files },
        { label: '使用权期内主播授权文件', value: data.use_right_auth_files },
        { label: '收益权期内主播授权文件', value: data.revenue_right_auth_files },
        { label: '作品著作权与内容资产处置权证明文件', value: data.copyright_files },
        { label: '商业合作与品牌合作证明文件', value: data.brand_cooperation_files }
      ])
    },
    mcnOwnershipTypes() {
      const data = isPlainObject(this.mcnDataComputed.account_assets) ? this.mcnDataComputed.account_assets : {}
      return this.ensureDisplayArray(data.ownership_types, 'ownership_types')
    },
    mcnStreamerRows() {
      const operation = isPlainObject(this.mcnDataComputed.operation) ? this.mcnDataComputed.operation : {}
      const list = Array.isArray(operation.streamers) ? operation.streamers : []
      return list.map(item => ({
        platformId: this.formatDisplayValue(item.platform_id),
        name: this.formatDisplayValue(item.name),
        totalFans: this.formatDisplayValue(item.total_fans),
        totalClicks: this.formatDisplayValue(item.total_clicks),
        lastYearLiveSessions: this.formatDisplayValue(item.last_year_live_sessions),
        lastYearProfit: this.formatDisplayValue(item.last_year_profit),
        avgViewers: this.formatDisplayValue(item.avg_viewers),
        fanGrowthRate: this.formatDisplayValue(item.fan_growth_rate),
        liveConversionRate: this.formatDisplayValue(item.live_conversion_rate),
        avgGmv: this.formatDisplayValue(item.avg_gmv),
        avgOrderValue: this.formatDisplayValue(item.avg_order_value),
        last12mGmv: this.formatDisplayValue(item.last_12m_gmv),
        verticalTrack: this.formatDisplayValue(item.vertical_track, 'vertical_track')
      }))
    },
    mcnOperationData() {
      return isPlainObject(this.mcnDataComputed.operation) ? this.mcnDataComputed.operation : {}
    },
    mcnCommercialData() {
      return isPlainObject(this.mcnDataComputed.commercial) ? this.mcnDataComputed.commercial : {}
    },
    mcnOperationSummaryFields() {
      const data = this.mcnOperationData
      return [
        this.buildField('机构年度总GMV', data.annual_gmv, 'annual_gmv'),
        this.buildField('机构年度总营收', data.annual_revenue, 'annual_revenue'),
        this.buildField('机构年度净利润', data.annual_net_profit, 'annual_net_profit'),
        this.buildField('近12个月直播场次', data.last_12m_live_sessions, 'last_12m_live_sessions'),
        this.buildField('场均观看人数', data.avg_viewers, 'avg_viewers'),
        this.buildField('总粉丝覆盖量', data.total_fan_coverage, 'total_fan_coverage')
      ]
    },
    mcnIncomeRatioFields() {
      const data = this.mcnOperationData
      return [
        this.buildField('佣金收入占比', data.commission_income_ratio, 'commission_income_ratio'),
        this.buildField('坑位费收入占比', data.slot_fee_income_ratio, 'slot_fee_income_ratio'),
        this.buildField('品牌营销收入占比', data.brand_marketing_income_ratio, 'brand_marketing_income_ratio'),
        this.buildField('其他收入占比', data.other_income_ratio, 'other_income_ratio'),
        this.buildField('运营成本率', data.operating_cost_ratio, 'operating_cost_ratio'),
        this.buildField('净利润率', data.net_profit_ratio, 'net_profit_ratio')
      ]
    },
    mcnRevenueForecastCards() {
      const data = this.mcnOperationData
      return [
        this.buildField('短期收益-未来6个月', data.short_term_6m, 'short_term_6m'),
        this.buildField('全生命周期收益-3年', data.lifecycle_3y, 'lifecycle_3y')
      ]
    },
    mcnRevenueBasisText() {
      return this.formatDisplayValue(this.mcnOperationData.revenue_basis, 'revenue_basis')
    },
    mcnCommercialForecastCards() {
      const commercial = this.mcnCommercialData
      return [
        this.buildField('短期收益-未来12个月', commercial.short_term, 'short_term'),
        this.buildField('全生命周期收益-5年', commercial.lifecycle, 'lifecycle')
      ]
    },
    mcnThirdPartySupportText() {
      return this.formatDisplayValue(this.mcnOperationData.third_party_support, 'third_party_support')
    },
    currentApplicationData() {
      if (!this.currentItem) return null
      return this.currentItem.userType === 'mcn' ? this.currentItem.mcnData : this.currentItem.individualData
    },
    applicationBlocks() {
      return this.buildDataBlocks(this.currentApplicationData, this.currentItem ? (this.currentItem.userType === 'mcn' ? '机构申请资料' : '个人申请资料') : '')
    },
    submitterFields() {
      if (!this.currentItem) return []

      const submitter = this.currentItem.submitter || {}
      const preferredKeys = ['name', 'nickname', 'real_name', 'phone', 'email', 'company', 'company_name']
      const fields = []
      const usedKeys = {}

      preferredKeys.forEach(key => {
        if (!this.isEmptyValue(submitter[key])) {
          usedKeys[key] = true
          fields.push({
            label: this.labelForKey(key),
            value: this.formatDisplayValue(submitter[key], key)
          })
        }
      })

      fields.push({
        label: '用户类型',
        value: this.currentItem.userTypeText
      })

      Object.keys(submitter).forEach(key => {
        if (usedKeys[key]) return
        if (this.isEmptyValue(submitter[key])) return
        if (Array.isArray(submitter[key]) || isPlainObject(submitter[key])) return

        fields.push({
          label: this.labelForKey(key),
          value: this.formatDisplayValue(submitter[key], key)
        })
      })

      return fields
    },
    reviewBlueprint() {
      return getReviewBlueprint(this.currentItem ? this.currentItem.userType : 'individual')
    },
    reviewGroups() {
      return this.reviewBlueprint.groups
    },
    standaloneFields() {
      return this.reviewBlueprint.standaloneFields
    },
    conclusionFields() {
      return this.reviewBlueprint.conclusionFields
    },
    showReviewForm() {
      return this.isPending || this.hasMeaningfulReviewData(this.reviewForm)
    },
    approvedHistory() {
      if (!this.currentItem || !Array.isArray(this.currentItem.approvedHistory)) return []

      return this.currentItem.approvedHistory.map(item => ({
        id: item.id,
        evaluateNo: item.evaluate_no || item.evaluation_no || '—',
        assetName: item.asset_name || item.assetName || '—',
        reportUrl: item.report_url || '',
        submittedAt: item.submitted_at || item.created_at || '—',
        handledAt: item.handled_at || '—',
        applicationBlocks: this.buildDataBlocks(item.userData, '提交资料'),
        reviewBlocks: this.buildDataBlocks(item.reviewData, '后台评估数据')
      }))
    }
  },
  created() {
    this.loadDetail()
  },
  watch: {
    '$route.params.id'() {
      this.loadDetail()
    }
  },
  methods: {
    async loadDetail() {
      this.loading = true
      this.currentItem = null

      try {
        const res = await assetEvaluationDetail(this.$route.params.id)
        const detail = this.extractDetail(res)

        if (!detail || !detail.id) {
          this.$message.error(this.getErrorMessage(res, '未找到对应的资产评估申请'))
          this.backToList()
          return
        }

        this.currentItem = this.normalizeDetail(detail)
        this.reviewForm = mergeReviewForm(this.currentItem.userType, this.currentItem.reviewData)
        this.editingValuation = false
        this.valuationDraftBackup = this.reviewForm.streamer_asset_valuation || ''
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '资产评估详情加载失败'))
        this.backToList()
      } finally {
        this.loading = false
      }
    },
    extractDetail(res) {
      if (!res || typeof res !== 'object' || res.message || res.detail || res.errors) {
        return null
      }

      if (res.data && !Array.isArray(res.data) && typeof res.data === 'object') {
        return res.data
      }

      return res
    },
    normalizeDetail(item) {
      const userType = item.user_type || item.userType || (item.mcn_data ? 'mcn' : 'individual')
      const submitter = this.pickSubmitter(item)
      const approvedHistory = Array.isArray(item.approved_history)
        ? item.approved_history.map(history => this.normalizeHistory(history, userType))
        : []

      return {
        id: item.id,
        evaluateNo: item.evaluate_no || item.evaluation_no || '—',
        assetName: item.asset_name || item.assetName || '—',
        assetCategory: item.asset_category || item.assetCategory || '—',
        userType,
        userTypeText: item.user_type_text || this.typeText(userType),
        status: item.status || '',
        statusText: item.status_text || this.statusText(item.status),
        submittedAt: item.submitted_at || item.created_at || '—',
        handledAt: item.handled_at || '',
        reviewDraftedAt: item.review_drafted_at || '',
        reportUrl: item.report_url || '',
        blockchainHash: item.blockchain_hash || item.blockchainHash || '',
        blockchainTxHash: item.blockchain_tx_hash || item.blockchainTxHash || '',
        rejectedReason: item.reject_reason || '',
        submitter,
        submitterName: submitter.name || submitter.nickname || submitter.real_name || item.submitter_name || item.user_name || '—',
        submitterPhone: submitter.phone || item.phone || '—',
        individualData: isPlainObject(item.individual_data) ? item.individual_data : {},
        mcnData: isPlainObject(item.mcn_data) ? item.mcn_data : {},
        reviewData: isPlainObject(item.review_data) ? item.review_data : {},
        approvedHistory
      }
    },
    normalizeHistory(item, userType) {
      return {
        id: item.id,
        evaluate_no: item.evaluate_no || item.evaluation_no || '',
        asset_name: item.asset_name || item.assetName || '',
        report_url: item.report_url || '',
        submitted_at: item.submitted_at || item.created_at || '',
        handled_at: item.handled_at || '',
        userData: isPlainObject(item.individual_data) ? item.individual_data : (isPlainObject(item.mcn_data) ? item.mcn_data : (isPlainObject(item.user_data) ? item.user_data : {})),
        reviewData: isPlainObject(item.review_data) ? item.review_data : {},
        userType
      }
    },
    pickSubmitter(item) {
      const candidates = [item.submitter, item.submitter_info, item.user, item.applicant]
      let submitter = {}

      for (let i = 0; i < candidates.length; i += 1) {
        if (isPlainObject(candidates[i])) {
          submitter = Object.assign({}, candidates[i])
          break
        }
      }

      if (!submitter.name && item.submitter_name) {
        submitter.name = item.submitter_name
      }

      if (!submitter.name && item.user_name) {
        submitter.name = item.user_name
      }

      if (!submitter.phone && item.phone) {
        submitter.phone = item.phone
      }

      if (!submitter.company_name && item.company_name) {
        submitter.company_name = item.company_name
      }

      return submitter
    },
    statusText(status) {
      return {
        draft: '草稿',
        pending: '待审核',
        approved: '已通过',
        rejected: '已驳回'
      }[status] || status || '—'
    },
    typeText(type) {
      return {
        individual: '个人',
        mcn: 'MCN机构'
      }[type] || type || '—'
    },
    statusClass(status) {
      return {
        status: true,
        'status-reviewing': status === 'draft',
        'status-pending': status === 'pending',
        'status-approved': status === 'approved',
        'status-rejected': status === 'rejected'
      }
    },
    backToList() {
      this.$router.push({
        path: '/applications/evaluation',
        query: Object.assign({}, this.$route.query)
      })
    },
    startEditValuation() {
      this.valuationDraftBackup = this.reviewForm.streamer_asset_valuation || ''
      this.editingValuation = true
    },
    finishEditValuation() {
      this.editingValuation = false
    },
    cancelEditValuation() {
      this.reviewForm.streamer_asset_valuation = this.valuationDraftBackup || ''
      this.editingValuation = false
    },
    async openReport(url) {
      if (!url) {
        this.$message.warning('当前记录暂无可下载报告')
        return
      }

      await downloadRemoteFile(url, {
        fileName: 'asset-evaluation-report.docx'
      })
    },
    isImageFile(url) {
      if (!url || typeof url !== 'string') return false

      const normalized = this.normalizeFileUrl(url)
      return /\.(png|jpg|jpeg|gif|webp|bmp)$/i.test(normalized)
    },
    normalizeFileUrl(url) {
      return String(url).split('?')[0].split('#')[0]
    },
    uniqueDisplayList(list) {
      const map = {}
      return (Array.isArray(list) ? list : []).filter(item => {
        if (!item || item === '—' || map[item]) {
          return false
        }

        map[item] = true
        return true
      })
    },
    openMediaPreview(media) {
      if (!media || !media.url) {
        this.$message.warning('当前附件不可预览')
        return
      }

      if (this.isImageFile(media.url)) {
        this.previewType = 'image'
        this.previewUrl = media.url
      } else {
        return downloadRemoteFile(media.url, {
          fileName: media.name
        })
      }

      this.previewTitle = media.name || '附件预览'
      this.previewDialogVisible = true
    },
    buildField(label, value, key, fullWidth) {
      return {
        label,
        value: this.formatDisplayValue(value, key),
        fullWidth: !!fullWidth
      }
    },
    ensureDisplayArray(value, key) {
      const list = Array.isArray(value) ? value : (this.isEmptyValue(value) ? [] : [value])
      return list.map(item => this.formatDisplayValue(item, key)).filter(item => item && item !== '—')
    },
    createFileGroups(configs) {
      return configs.reduce((result, config) => {
        const list = Array.isArray(config.value) ? config.value.filter(Boolean) : (config.value ? [config.value] : [])

        if (list.length) {
          result.push({
            label: config.label,
            items: list.map(item => this.normalizeFileItem(item))
          })
        }

        return result
      }, [])
    },
    labelForKey(key) {
      return FIELD_LABELS[key] || key.replace(/_/g, ' ')
    },
    enumTypeForKey(key) {
      return KEY_ENUM_MAP[key] || ''
    },
    mapEnumLabel(key, value) {
      const enumType = this.enumTypeForKey(key)
      const enumMap = enumType ? ENUM_LABELS[enumType] : null

      if (!enumMap) {
        return value
      }

      return enumMap[value] || value
    },
    formatDisplayValue(value, key) {
      if (this.isEmptyValue(value)) return '—'
      if (typeof value === 'boolean') return value ? '是' : '否'
      if (Array.isArray(value)) return value.map(item => this.formatDisplayValue(item, key)).join('、') || '—'
      if (isPlainObject(value)) return JSON.stringify(value)

      const mappedValue = key ? this.mapEnumLabel(key, value) : value
      const text = String(mappedValue)
      const unit = key ? FIELD_UNIT_MAP[key] : ''
      return unit ? this.appendUnit(text, unit) : text
    },
    appendUnit(value, unit) {
      if (!value || !unit) return value

      const text = String(value).trim()

      if (!text) return text
      if (unit === '%') {
        return /(%|％)$/u.test(text) ? text : (text + '%')
      }

      return text.endsWith(unit) ? text : (text + unit)
    },
    isEmptyValue(value) {
      return value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)
    },
    isPrimitiveArray(list) {
      for (let i = 0; i < list.length; i += 1) {
        if (isPlainObject(list[i]) || Array.isArray(list[i])) {
          return false
        }
      }

      return true
    },
    isObjectArray(list) {
      for (let i = 0; i < list.length; i += 1) {
        if (!isPlainObject(list[i])) {
          return false
        }
      }

      return true
    },
    isLongText(value) {
      return typeof value === 'string' && value.length > 40
    },
    isFileLikeKey(key) {
      return /(file|files|attachment|attachments|material|materials|proof|proofs|report|image|images|url)$/i.test(key)
    },
    isFileLikeString(value) {
      if (!value || typeof value !== 'string') return false

      return /^(https?:\/\/|\/)/i.test(value) || /\.(pdf|png|jpg|jpeg|gif|webp|doc|docx|xls|xlsx|ppt|pptx|zip)$/i.test(value.split('?')[0])
    },
    normalizeFileItem(value) {
      if (isPlainObject(value)) {
        const url = value.url || value.path || value.file_url || value.file || ''
        return {
          name: value.name || value.file_name || this.extractFileName(url),
          url: url || value.name || ''
        }
      }

      return {
        name: this.extractFileName(value),
        url: value
      }
    },
    extractFileName(value) {
      if (!value || typeof value !== 'string') return '附件'

      const normalized = value.split('?')[0]
      const segments = normalized.split('/')
      return segments[segments.length - 1] || value
    },
    createTableBlock(title, rows, sourceKey) {
      const firstRow = rows[0] || {}
      const columns = Object.keys(firstRow).map(key => ({
        key,
        label: this.resolveTableColumnLabel(sourceKey, key)
      }))

      return {
        type: 'table',
        title,
        columns,
        rows
      }
    },
    resolveTableColumnLabel(sourceKey, key) {
      if (sourceKey === 'platforms' && key === 'nickname') {
        return '主播昵称'
      }

      if (sourceKey === 'streamers' && key === 'name') {
        return '主播名称'
      }

      return this.labelForKey(key === 'nickname' ? 'nickname_text' : key)
    },
    buildDataBlocks(source, title) {
      if (!isPlainObject(source)) return []

      const blocks = []
      const gridFields = []
      const textFields = []
      const tagGroups = []
      const fileGroups = []

      Object.keys(source).forEach(key => {
        const value = source[key]

        if (this.isEmptyValue(value)) {
          return
        }

        const label = this.labelForKey(key)

        if (Array.isArray(value)) {
          const cleaned = value.filter(item => !this.isEmptyValue(item))

          if (!cleaned.length) {
            return
          }

          if (this.isObjectArray(cleaned)) {
            blocks.push(this.createTableBlock(label, cleaned, key))
            return
          }

          if (this.isPrimitiveArray(cleaned)) {
            if (this.isFileLikeKey(key) || cleaned.every(item => this.isFileLikeString(item))) {
              fileGroups.push({
                label,
                items: cleaned.map(item => this.normalizeFileItem(item))
              })
            } else {
              tagGroups.push({
                label,
                values: cleaned.map(item => this.formatDisplayValue(item, key))
              })
            }
            return
          }

          textFields.push({
            label,
            value: cleaned.map(item => this.formatDisplayValue(item, key)).join('、')
          })
          return
        }

        if (isPlainObject(value)) {
          const nestedBlocks = this.buildDataBlocks(value, label)

          Array.prototype.push.apply(blocks, nestedBlocks)
          return
        }

        if (this.isFileLikeKey(key) && this.isFileLikeString(value)) {
          fileGroups.push({
            label,
            items: [this.normalizeFileItem(value)]
          })
          return
        }

        if (this.isLongText(value)) {
          textFields.push({
            label,
            value: this.formatDisplayValue(value, key)
          })
          return
        }

        gridFields.push({
          label,
          value: this.formatDisplayValue(value, key)
        })
      })

      const prefixBlocks = []

      if (gridFields.length) {
        prefixBlocks.push({
          type: 'grid',
          title,
          fields: gridFields
        })
      }

      if (tagGroups.length) {
        prefixBlocks.push({
          type: 'tags',
          title: gridFields.length ? '' : title,
          groups: tagGroups
        })
      }

      if (textFields.length) {
        prefixBlocks.push({
          type: 'text',
          title: gridFields.length || tagGroups.length ? '' : title,
          fields: textFields
        })
      }

      if (fileGroups.length) {
        prefixBlocks.push({
          type: 'files',
          title: title ? title + '附件' : '附件',
          groups: fileGroups
        })
      }

      return prefixBlocks.concat(blocks)
    },
    hasMeaningfulReviewData(source) {
      const blueprint = this.reviewBlueprint

      for (let i = 0; i < blueprint.groups.length; i += 1) {
        const group = blueprint.groups[i]

        for (let j = 0; j < group.rows.length; j += 1) {
          const row = group.rows[j]
          const item = source[group.key] && source[group.key][row.key] ? source[group.key][row.key] : createScoreItem()

          if (item.score !== '' || (item.comment && item.comment.trim())) {
            return true
          }
        }
      }

      for (let k = 0; k < blueprint.standaloneFields.length; k += 1) {
        const field = blueprint.standaloneFields[k]
        const value = source[field.key]

        if (value !== '' && value !== null && value !== undefined) {
          return true
        }
      }

      for (let x = 0; x < blueprint.conclusionFields.length; x += 1) {
        const conclusion = blueprint.conclusionFields[x]
        const text = source[conclusion.key]

        if (text && String(text).trim()) {
          return true
        }
      }

      return false
    },
    isValidScore(score) {
      return score !== '' && score >= 1 && score <= 10 && Math.floor(score) === score
    },
    normalizeText(value) {
      return value === null || value === undefined ? '' : String(value).trim()
    },
    validateTextLength(label, value, maxLength) {
      if (maxLength && value.length > maxLength) {
        return label + '不能超过' + maxLength + '个字符'
      }

      return ''
    },
    buildReviewPayload(requireComplete) {
      const blueprint = this.reviewBlueprint
      const payload = {}

      for (let i = 0; i < blueprint.groups.length; i += 1) {
        const group = blueprint.groups[i]
        const groupPayload = {}

        for (let j = 0; j < group.rows.length; j += 1) {
          const row = group.rows[j]
          const current = this.reviewForm[group.key][row.key]
          const score = normalizeScoreInput(current.score)
          const comment = this.normalizeText(current.comment)
          const touched = score !== '' || !!comment
          const commentError = this.validateTextLength('“' + row.label + '”评语', comment, 200)

          if (commentError) {
            return { valid: false, message: commentError }
          }

          if (requireComplete) {
            if (!this.isValidScore(score)) {
              return { valid: false, message: '请填写“' + row.label + '”的 1-10 整数评分' }
            }

            if (!comment) {
              return { valid: false, message: '请填写“' + row.label + '”的评语' }
            }

            groupPayload[row.key] = { score, comment }
            continue
          }

          if (touched) {
            if (score !== '' && !this.isValidScore(score)) {
              return { valid: false, message: '“' + row.label + '”评分需为 1-10 的整数' }
            }

            groupPayload[row.key] = {
              score: score === '' ? null : score,
              comment
            }
          }
        }

        if (requireComplete || Object.keys(groupPayload).length) {
          payload[group.key] = groupPayload
        }
      }

      for (let k = 0; k < blueprint.standaloneFields.length; k += 1) {
        const field = blueprint.standaloneFields[k]
        const currentValue = this.reviewForm[field.key]

        if (field.type === 'score') {
          const score = normalizeScoreInput(currentValue)

          if (requireComplete) {
            if (!this.isValidScore(score)) {
              return { valid: false, message: '请填写“' + field.label + '”的 1-10 整数评分' }
            }

            payload[field.key] = score
            continue
          }

          if (score !== '') {
            if (!this.isValidScore(score)) {
              return { valid: false, message: '“' + field.label + '”评分需为 1-10 的整数' }
            }

            payload[field.key] = score
          }

          continue
        }

        const text = this.normalizeText(currentValue)

        if (requireComplete) {
          if (!text) {
            return { valid: false, message: '请填写“' + field.label + '”' }
          }

          const textError = this.validateTextLength(field.label, text, field.maxlength)
          if (textError) {
            return { valid: false, message: textError }
          }

          payload[field.key] = text
          continue
        }

        if (text) {
          const textError = this.validateTextLength(field.label, text, field.maxlength)
          if (textError) {
            return { valid: false, message: textError }
          }

          payload[field.key] = text
        }
      }

      for (let x = 0; x < blueprint.conclusionFields.length; x += 1) {
        const field = blueprint.conclusionFields[x]
        const text = this.normalizeText(this.reviewForm[field.key])

        if (requireComplete) {
          if (!text) {
            return { valid: false, message: '请填写“' + field.label + '”' }
          }

          const textError = this.validateTextLength(field.label, text, field.maxlength)
          if (textError) {
            return { valid: false, message: textError }
          }

          payload[field.key] = text
          continue
        }

        if (text) {
          const textError = this.validateTextLength(field.label, text, field.maxlength)
          if (textError) {
            return { valid: false, message: textError }
          }

          payload[field.key] = text
        }
      }

      return {
        valid: true,
        payload
      }
    },
    async saveDraft() {
      const result = this.buildReviewPayload(false)

      if (!result.valid) {
        this.$message.error(result.message)
        return
      }

      this.submittingDraft = true

      try {
        const res = await assetEvaluationReviewDraft(this.currentItem.id, {
          review_data: result.payload
        })

        if (this.isRequestFailed(res)) {
          this.$message.error(this.getErrorMessage(res, '审核草稿保存失败'))
          return
        }

        this.$message.success('审核草稿已保存')
        await this.loadDetail()
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '审核草稿保存失败'))
      } finally {
        this.submittingDraft = false
      }
    },
    async submitApproved() {
      const result = this.buildReviewPayload(true)

      if (!result.valid) {
        this.$message.error(result.message)
        return
      }

      try {
        await this.$confirm('确认审核通过并生成评估报告吗？', '提示', {
          type: 'warning'
        })
      } catch (error) {
        return
      }

      this.submittingProcess = true
      this.processingAction = 'approved'

      try {
        const res = await assetEvaluationProcess(this.currentItem.id, {
          status: 'approved',
          review_data: result.payload
        })

        if (this.isRequestFailed(res)) {
          this.$message.error(this.getErrorMessage(res, '资产评估审核通过失败'))
          return
        }

        this.$message.success('资产评估已审核通过')
        await this.loadDetail()
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '资产评估审核通过失败'))
      } finally {
        this.submittingProcess = false
        this.processingAction = ''
      }
    },
    async submitRejected() {
      let promptResult = null

      try {
        promptResult = await this.$prompt('请输入驳回原因', '驳回申请', {
          confirmButtonText: '确认驳回',
          cancelButtonText: '取消',
          inputType: 'textarea',
          inputPattern: /\S+/,
          inputErrorMessage: '请填写驳回原因'
        })
      } catch (error) {
        return
      }

      const rejectReason = this.normalizeText(promptResult.value)

      if (!rejectReason) {
        this.$message.error('请填写驳回原因')
        return
      }

      this.submittingProcess = true
      this.processingAction = 'rejected'

      try {
        const res = await assetEvaluationProcess(this.currentItem.id, {
          status: 'rejected',
          reject_reason: rejectReason
        })

        if (this.isRequestFailed(res)) {
          this.$message.error(this.getErrorMessage(res, '资产评估驳回失败'))
          return
        }

        this.$message.success('资产评估已驳回')
        await this.loadDetail()
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '资产评估驳回失败'))
      } finally {
        this.submittingProcess = false
        this.processingAction = ''
      }
    },
    isRequestFailed(res) {
      return typeof res === 'string' || !!(res && typeof res === 'object' && (res.message || res.detail || res.errors))
    },
    getErrorMessage(error, fallback) {
      if (!error) return fallback
      if (typeof error === 'string') return error
      if (error.message) return error.message
      if (error.detail) return error.detail
      if (error.errors) {
        const firstKey = Object.keys(error.errors)[0]
        const firstError = firstKey ? error.errors[firstKey] : ''

        if (Array.isArray(firstError)) {
          return firstError[0] || fallback
        }

        return firstError || fallback
      }

      return fallback
    }
  }
}
</script>

<style scoped>
.evaluation-detail-page {
  gap: 18px;
}

.evaluation-detail-page__topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  min-height: 70px;
  margin: -20px -20px 0;
  padding: 18px 24px 20px;
  background: #e9eef5;
}

.evaluation-detail-page__title h1 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  line-height: 1.2;
}

.evaluation-detail-page__title p {
  margin-top: 10px;
  font-size: 13px;
  color: #334155;
}

.title-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  font-size: 18px;
}

.evaluation-detail-page__topbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.evaluation-state-card {
  text-align: center;
  color: var(--cog-text-secondary);
}

.evaluation-hero {
  align-items: stretch;
}

.evaluation-hero--summary {
  display: block;
  padding: 20px 24px;
}

.evaluation-hero__main {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.evaluation-hero__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.evaluation-hero__title {
  margin: 0;
  font-size: 24px;
  line-height: 1.2;
}

.summary-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  margin-top: 18px;
  border: 1px solid rgba(129, 140, 248, 0.14);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.summary-strip--with-evidence {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.summary-strip__item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 88px;
  padding: 16px 18px;
  border-right: 1px solid var(--cog-border-light);
}

.summary-strip__item:last-child {
  border-right: none;
}

.summary-strip__item span {
  font-size: 12px;
  color: var(--cog-text-secondary);
}

.summary-strip__item strong {
  font-size: 14px;
  color: #0f172a;
  line-height: 1.5;
  word-break: break-word;
}

.detail-section-card {
  padding: 20px 22px;
}

.detail-panel {
  display: grid;
  gap: 16px;
}

.detail-panel__grid {
  gap: 14px 20px;
}

.detail-panel__grid--personal {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.detail-panel__grid--mcn {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.detail-panel__grid--mcn-basic-split {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.detail-panel__grid--operation {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.tag-line {
  display: grid;
  grid-template-columns: 73px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
}

.tag-line__label {
  font-size: 12px;
  color: var(--cog-text-muted);
  line-height: 30px;
}

.tag-line__content {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.operation-subsection__title,
.review-conclusion-card__title,
.review-group-card__subhead {
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.mcn-basic-layout {
  gap: 18px;
}

.mcn-basic-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 28px;
}

.mcn-basic-summary__item {
  min-width: 0;
}

.mcn-basic-panel {
  padding: 14px 16px;
  border-radius: 12px;
  background: #f8fafc;
}

.mcn-basic-panel__title {
  margin-bottom: 14px;
  font-size: 14px;
  font-weight: 700;
  color: #64748b;
}

.valuation-entry-card {
  padding: 16px 18px;
  margin-bottom: 16px;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  background: #eff6ff;
}

.valuation-entry-card__header--row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.valuation-entry-card__section + .valuation-entry-card__section,
.valuation-entry-card__section + .valuation-entry-card__header {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #bfdbfe;
}

.valuation-entry-card__section-title {
  margin-bottom: 14px;
  font-size: 15px;
  font-weight: 700;
  color: #2563eb;
}

.valuation-entry-card__submitted-value,
.valuation-entry-card__metric,
.valuation-entry-card__text-field {
  display: grid;
  gap: 6px;
}

.valuation-entry-card__submitted-value span,
.valuation-entry-card__metric span,
.valuation-entry-card__text-field > span {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}

.valuation-entry-card__submitted-value strong {
  font-size: 20px;
  color: #2563eb;
}

.valuation-entry-card__metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px 18px;
}

.valuation-entry-card__metric {
  min-height: 68px;
  padding: 12px;
  border: 1px solid #dbeafe;
  border-radius: var(--cog-radius-sm);
  background: #fff;
}

.valuation-entry-card__metric strong {
  font-size: 15px;
  color: #334155;
}

.valuation-entry-card__text-field + .valuation-entry-card__text-field {
  margin-top: 14px;
}

.valuation-entry-card__text-field p {
  min-height: 42px;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid #dbeafe;
  border-radius: var(--cog-radius-sm);
  background: #fff;
  color: #475569;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.valuation-entry-card__header {
  display: grid;
  gap: 8px;
}

.valuation-entry-card__label {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.valuation-entry-card__value {
  margin-top: 10px;
  font-size: 34px;
  line-height: 1.1;
  font-weight: 700;
  color: #2563eb;
}

.valuation-entry-card__note {
  margin-top: 10px;
  font-size: 13px;
  line-height: 1.7;
  color: #475569;
}

.valuation-entry-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.valuation-entry-card__control {
  margin-top: 14px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
  line-height: 1.2;
}

.review-subsection + .review-subsection {
  margin-top: 18px;
}

.review-subsection__title {
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.section-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 18px;
}

.section-head__index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #eef2ff;
  color: var(--cog-primary);
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.section-head__content h3 {
  margin: 0;
  font-size: 16px;
}

.section-head__content p {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--cog-text-secondary);
}

.file-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.file-grid__item {
  padding: 14px;
  border: 1px solid var(--cog-border-light);
  border-radius: 12px;
  background: #fff;
}

.file-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.file-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border: 1px solid var(--cog-border);
  border-radius: 999px;
  background: #f8fafc;
  font-size: 13px;
  color: #1e293b;
}

.file-chip--link {
  text-decoration: none;
}

.empty-placeholder {
  padding: 18px;
  border: 1px dashed var(--cog-border);
  border-radius: 12px;
  text-align: center;
  color: var(--cog-text-secondary);
  background: #f8fafc;
}

.empty-placeholder--spacious {
  margin-top: 10px;
}

.empty-placeholder--compact {
  padding: 14px;
}

.review-group-card + .review-group-card {
  margin-top: 18px;
}

.review-score-table__score {
  width: 132px;
}

.review-score-table__dimension {
  min-width: 140px;
  font-weight: 600;
  color: #0f172a;
}

.review-score-table__dimension--summary {
  color: #1d4ed8;
}

.review-score-table__description {
  min-width: 240px;
  color: #475569;
  line-height: 1.6;
}

.review-score-table__row--summary td {
  background: #eff6ff;
}

.review-score-total {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  margin-top: 12px;
  border-radius: 12px;
  background: #eff6ff;
  font-size: 13px;
  font-weight: 600;
}

.review-conclusion-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.review-conclusion-card {
  padding: 18px;
  border: 1px solid var(--cog-border-light);
  border-radius: 14px;
  background: #f8fafc;
}

.review-conclusion-card--final {
  margin-top: 0;
}

.review-conclusion-grid--final {
  gap: 18px;
}

.form-group {
  min-width: 0;
}

.form-group--conclusion {
  margin-top: 4px;
}


.form-group--conclusion-block {
  padding: 16px;
  border: 1px solid var(--cog-border-light);
  border-radius: 12px;
  background: #fff;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
}

.form-group input,
.form-group textarea,
.review-score-table textarea,
.score-input {
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid var(--cog-border);
  border-radius: var(--cog-radius-sm);
  font-family: inherit;
  font-size: 13px;
  color: #1e293b;
  background: #fff;
}

.form-group textarea,
.review-score-table textarea {
  resize: vertical;
}

.score-input {
  max-width: 84px;
  text-align: center;
}

.score-input--wide {
  max-width: 120px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.readonly-box {
  min-height: 44px;
  padding: 10px 12px;
  border: 1.5px solid var(--cog-border);
  border-radius: var(--cog-radius-sm);
  background: #fff;
  color: #1e293b;
  line-height: 1.7;
  white-space: pre-wrap;
}

.full-width {
  grid-column: 1 / -1;
}

.forecast-panel {
  padding: 16px;
  border: 1px solid var(--cog-border-light);
  border-radius: 12px;
  background: #f8fafc;
}

.forecast-panel__title {
  margin-bottom: 14px;
  font-size: 14px;
  font-weight: 700;
}

.forecast-panel__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.forecast-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid var(--cog-border-light);
}

.forecast-item span {
  font-size: 12px;
  color: var(--cog-text-secondary);
}

.forecast-item strong {
  font-size: 18px;
  color: #0f172a;
}

.forecast-panel__basis {
  margin-top: 14px;
}

.forecast-panel--mcn-submitted {
  margin-bottom: 16px;
}

.result-inline {
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--cog-border-light);
  background: #fff;
  line-height: 1.7;
}

.result-inline--danger {
  background: #fff7f7;
  border-color: #fecaca;
  color: #b91c1c;
}

.input-affix {
  display: flex;
  align-items: center;
  gap: 8px;
}

.input-affix input {
  flex: 1;
}

.input-affix__unit {
  font-size: 13px;
  color: var(--cog-text-secondary);
  white-space: nowrap;
}

.media-preview {
  min-height: 420px;
}

.media-preview__image {
  display: block;
  max-width: 100%;
  max-height: 76vh;
  margin: 0 auto;
  object-fit: contain;
}

.evaluation-action-bar {
  bottom: 20px;
  justify-content: flex-end;
  padding: 14px 16px;
}

.action-bar__btns--right {
  display: flex;
  width: auto;
  margin-left: auto;
  gap: 12px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.btn-loading-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: evaluation-btn-spin 0.75s linear infinite;
}

@keyframes evaluation-btn-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .summary-strip,
  .detail-panel__grid--personal,
  .detail-panel__grid--mcn,
  .detail-panel__grid--mcn-basic-split,
  .mcn-basic-summary,
  .file-grid,
  .forecast-panel__grid,
  .review-conclusion-grid,
  .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .evaluation-detail-page__topbar {
    margin: -16px -16px 0;
    padding: 16px;
  }

  .evaluation-detail-page__topbar-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .valuation-entry-card__header--row {
    flex-direction: column;
  }

  .section-head {
    align-items: center;
  }

  .tag-line {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .review-score-total {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}

@media (max-width: 900px) {
  .evaluation-action-bar {
    align-items: stretch;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/applications/evaluation/detail.vue